/**
 * Render a calc cell's computed result THROUGH its author formatting, consumed
 * by `dangerouslySetInnerHTML` in the reader.
 *
 * The "treat-as-text" model: formatting lives in `cell.calcFormatHtml` — an HTML
 * fragment written via the SAME rich-text path text cells use
 * (`tableApplyFroalaContentSettings`), e.g.
 * `<p><span style="…"><strong><u>VALUE</u></strong></span></p>`. The computed
 * value is injected into the fragment's deepest text node at render time, so
 * every format (bold/italic/underline/strike/color/size/line-height/highlight)
 * works with no per-format code. `cell.content` (pre-formula text for
 * restore-on-clear) is never involved here.
 *
 * Precedence:
 *   - `calcFormatHtml` present  → inject the value into it
 *   - absent / empty / blank    → bare `<p>value</p>`
 *
 * Self-contained (no `@editor` imports) so it mirrors cleanly to the reader
 * repo. Assumes a client DOM (the reader is a browser SPA; jsdom in tests).
 */

const HTML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);

// Void elements can't hold children — never descend into them when placing the
// value (see the no-text-node fallback below).
const VOID_TAGS = new Set(["BR", "IMG", "HR", "INPUT", "WBR"]);

const compute = (
  calcFormatHtml: string | undefined,
  calcDisplay: string,
): string => {
  const html = calcFormatHtml?.trim();
  // No formatting → bare paragraph (table-level CSS defaults still apply). This
  // path builds a string directly, so escape the value manually.
  if (!html) return `<p>${escapeHtml(calcDisplay)}</p>`;

  const doc = new DOMParser().parseFromString(html, "text/html");
  const body = doc.body;
  if (!body.firstElementChild) return `<p>${escapeHtml(calcDisplay)}</p>`;

  // Inject the RAW value: the formatter nests the placeholder in a single-child
  // chain of inline tags, so the first text node IS the placeholder. Setting
  // `nodeValue` (not innerHTML) means serialization escapes it exactly once —
  // do NOT pre-escape here or it would double-escape.
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  // Skip pretty-print whitespace between tags so the value replaces the real
  // placeholder text, not the indentation before it.
  let textNode = walker.nextNode();
  while (textNode && !textNode.nodeValue?.trim()) {
    textNode = walker.nextNode();
  }
  if (textNode) {
    textNode.nodeValue = calcDisplay;
  } else {
    // No text node (e.g. `<p><br></p>`): append the value to the innermost
    // element that can hold text. Don't descend INTO a void element like
    // `<br>` (appending to it wouldn't serialize), so the value lands on its
    // parent block.
    let el: Element = body.firstElementChild;
    // A void top-level element (e.g. a bare `<br>`) can't hold text, so
    // appending would silently drop the value — fall back to a plain wrapper.
    if (VOID_TAGS.has(el.tagName)) return `<p>${escapeHtml(calcDisplay)}</p>`;
    while (
      el.firstElementChild &&
      !VOID_TAGS.has(el.firstElementChild.tagName)
    ) {
      el = el.firstElementChild;
    }
    el.appendChild(doc.createTextNode(calcDisplay));
  }
  return body.innerHTML;
};

// Memoize: this runs per calc cell per render, so a large table must not
// re-parse author HTML every frame. A NUL separator between the two inputs
// avoids key collisions (a space separator could conflate distinct pairs, e.g.
// ("<p>x</p>", "a b") vs ("<p>x</p> a", "b")). A small cap bounds the map.
const MEMO_CAP = 500;
const memo = new Map<string, string>();

export const renderCalcResult = (
  calcFormatHtml: string | undefined,
  calcDisplay: string,
): string => {
  const key = `${calcFormatHtml ?? ""}\u0000${calcDisplay}`;
  const cached = memo.get(key);
  if (cached !== undefined) return cached;

  const result = compute(calcFormatHtml, calcDisplay);
  memo.set(key, result);
  if (memo.size > MEMO_CAP) {
    const oldest = memo.keys().next().value;
    if (oldest !== undefined) memo.delete(oldest);
  }
  return result;
};
